import { useState } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Icon } from "@core/assets";
import {
  Button,
  ConfirmDialog,
  DateField,
  ErrorPanel,
  NumberField,
  PageHeader,
  Select,
  SettingsSection,
  SkeletonRows,
  TextField,
  type SelectOption,
} from "@core/components";
import { hasAction } from "@core/permissions";
import { fieldErrorKey } from "@core/utils";
import { isArchived, type Cohort } from "../../_shared";
import { useAdministrators } from "../../create/hooks";
import { useCohort } from "../../detail";
import {
  administratorChanged,
  CohortSettingsFormSchema,
  diffSettings,
  settingsValuesOf,
  useArchiveCohort,
  useRestoreCohort,
  useSaveSettings,
} from "../../manage";
import { classifyManageFailure } from "../../manage/services/failure";

type FormErrorKey =
  | "create.errors.name-required"
  | "create.errors.places-invalid"
  | "create.errors.waitlist-invalid"
  | "create.errors.start-required"
  | "create.errors.end-required"
  | "create.errors.end-before-start"
  | "settings.errors.count-invalid";

const FORM_ID = "cohort-settings-form";

function SettingsForm({ cohort }: { cohort: Cohort }) {
  const { t } = useTranslation("cohorts");
  const navigate = useNavigate();
  const save = useSaveSettings(cohort);
  const archive = useArchiveCohort(cohort);
  const restore = useRestoreCohort();
  const administrators = useAdministrators();
  const [isConfirmingArchive, setConfirmingArchive] = useState(false);

  const canEdit =
    hasAction(cohort.available_actions, "edit") && !isArchived(cohort);
  const canAppoint =
    hasAction(cohort.available_actions, "appoint_responsible_administrator") &&
    !isArchived(cohort);
  const canArchive = hasAction(cohort.available_actions, "archive");
  const canRestore = hasAction(cohort.available_actions, "restore");
  const showTeam = hasAction(
    cohort.available_actions,
    "appoint_responsible_administrator"
  );
  const showArchive = canArchive || canRestore;

  const form = useForm({
    defaultValues: settingsValuesOf(cohort),
    validators: { onSubmit: CohortSettingsFormSchema },
    onSubmit: ({ value }) => {
      save.mutate({
        changes: diffSettings(cohort, value),
        administratorId: administratorChanged(cohort, value)
          ? value.administratorId
          : null,
      });
    },
  });

  const values = useStore(form.store, (state) => state.values);
  const hasChanges =
    Object.keys(diffSettings(cohort, values)).length > 0 ||
    administratorChanged(cohort, values);

  // A refusal belongs to the values it was made for: editing anything clears it.
  const failure = save.error ? classifyManageFailure(save.error) : null;
  const attemptOf = (changes: object, administratorId: string | null): string =>
    JSON.stringify([changes, administratorId ?? ""]);
  const failureApplies =
    failure !== null &&
    save.variables !== undefined &&
    attemptOf(save.variables.changes, save.variables.administratorId) ===
      attemptOf(
        diffSettings(cohort, values),
        administratorChanged(cohort, values) ? values.administratorId : null
      );
  const serverError = (kind: string): boolean =>
    failureApplies && failure.kind === kind;

  // The current Administrator stays in the list even if the team list no longer offers them.
  const options: SelectOption[] = (administrators.data ?? []).map((admin) => ({
    id: admin.id,
    label: admin.display_name,
    person: { email: admin.email },
  }));
  const current = cohort.responsible_administrator;
  if (current && !options.some((option) => option.id === current.id)) {
    options.unshift({
      id: current.id,
      label: current.full_name,
      person: {},
    });
  }

  const numberField = (
    name:
      | "places"
      | "waitlistPlaces"
      | "interviewMarginPercent"
      | "responseDelayDays",
    label: string
  ) => (
    <form.Field name={name}>
      {(field) => {
        const errorKey = fieldErrorKey<FormErrorKey>(field.state.meta.errors);
        return (
          <NumberField
            label={label}
            minValue={0}
            value={field.state.value}
            onChange={(value) => field.handleChange(value)}
            onBlur={field.handleBlur}
            isDisabled={!canEdit || save.isPending}
            errorMessage={errorKey ? t(errorKey) : undefined}
          />
        );
      }}
    </form.Field>
  );

  const dateField = (name: "startDate" | "endDate", label: string) => (
    <form.Field name={name}>
      {(field) => {
        const errorKey = fieldErrorKey<FormErrorKey>(field.state.meta.errors);
        return (
          <DateField
            label={label}
            value={field.state.value}
            onChange={(value) => field.handleChange(value)}
            onBlur={field.handleBlur}
            isDisabled={!canEdit || save.isPending}
            errorMessage={
              errorKey
                ? t(errorKey)
                : name === "endDate" && serverError("end_before_start")
                  ? t("create.errors.end-before-start")
                  : undefined
            }
          />
        );
      }}
    </form.Field>
  );

  const submitButton =
    canEdit || canAppoint ? (
      <Button
        type="submit"
        form={FORM_ID}
        isPending={save.isPending}
        isDisabled={!hasChanges || save.isPending}
      >
        {t("settings.save")}
      </Button>
    ) : null;

  return (
    <>
      <PageHeader
        title={t("settings.title")}
        onBack={() => {
          void navigate({
            to: "/cohorts/$cohortId",
            params: { cohortId: cohort.id },
          });
        }}
        actions={submitButton}
      />

      <form
        id={FORM_ID}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
        className="flex flex-col"
      >
        <SettingsSection title={t("settings.sections.general")}>
          <form.Field name="name">
            {(field) => {
              const errorKey = fieldErrorKey<FormErrorKey>(
                field.state.meta.errors
              );
              return (
                <TextField
                  label={t("create.name")}
                  autoComplete="off"
                  value={field.state.value}
                  onChange={(value) => field.handleChange(value)}
                  onBlur={field.handleBlur}
                  isDisabled={!canEdit || save.isPending}
                  errorMessage={
                    errorKey
                      ? t(errorKey)
                      : serverError("name_invalid")
                        ? t("create.errors.name-required")
                        : undefined
                  }
                />
              );
            }}
          </form.Field>
          <TextField
            label={t("settings.code")}
            value={cohort.code}
            isReadOnly
          />
        </SettingsSection>

        <SettingsSection title={t("settings.sections.places")}>
          <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
            {numberField("places", t("create.places"))}
            {numberField("waitlistPlaces", t("create.waitlist"))}
            {numberField(
              "interviewMarginPercent",
              t("settings.interview-margin")
            )}
            {numberField("responseDelayDays", t("settings.response-delay"))}
          </div>
        </SettingsSection>

        <SettingsSection title={t("settings.sections.programme")}>
          <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
            {dateField("startDate", t("create.start"))}
            {dateField("endDate", t("create.end"))}
          </div>
        </SettingsSection>

        {showTeam ? (
          <SettingsSection title={t("settings.sections.team")}>
            <form.Field name="administratorId">
              {(field) => (
                <Select
                  label={t("create.administrator")}
                  options={options}
                  placeholder={t("create.administrator-placeholder")}
                  selectedKey={field.state.value || null}
                  onSelectionChange={(selected) =>
                    field.handleChange(
                      selected === null ? "" : String(selected)
                    )
                  }
                  isDisabled={!canAppoint || save.isPending}
                  errorMessage={
                    serverError("administrator_unavailable")
                      ? t("create.errors.administrator-unavailable")
                      : undefined
                  }
                />
              )}
            </form.Field>
          </SettingsSection>
        ) : null}

        {showArchive ? (
          <SettingsSection title={t("settings.sections.archive")}>
            <div className="flex flex-wrap gap-sm">
              {canArchive && !isArchived(cohort) ? (
                <Button
                  variant="secondary"
                  onPress={() => {
                    setConfirmingArchive(true);
                  }}
                >
                  <Icon name="archive" size={16} />
                  {t("archive.button")}
                </Button>
              ) : null}
              {canRestore && isArchived(cohort) ? (
                <Button
                  variant="secondary"
                  isPending={restore.isPending}
                  isDisabled={restore.isPending}
                  onPress={() => {
                    restore.mutate(cohort);
                  }}
                >
                  <Icon name="archive" size={16} />
                  {t("archive.restore")}
                </Button>
              ) : null}
            </div>
          </SettingsSection>
        ) : null}
      </form>

      <ConfirmDialog
        isOpen={isConfirmingArchive}
        onOpenChange={setConfirmingArchive}
        title={t("archive.confirm.title", { name: cohort.name })}
        description={t("archive.confirm.description")}
        confirmLabel={t("archive.confirm.submit")}
        cancelLabel={t("create.cancel")}
        isPending={archive.isPending}
        onConfirm={() => {
          archive.mutate(undefined, {
            onSuccess: () => {
              setConfirmingArchive(false);
              void navigate({ to: "/cohorts" });
            },
            onError: () => {
              setConfirmingArchive(false);
            },
          });
        }}
      />
    </>
  );
}

/**
 * « Paramètres » of a cohort (Figma "Cohort settings" and its variants): Général (name, and the
 * code, read-only), Places, Programme, Équipe (the responsible Administrator) and Archivage, each
 * a section with a divider. « Enregistrer » stays greyed out until something changes.
 *
 * Which sections and controls show follows the cohort's own `available_actions`: the responsible
 * Administrator edits the details but has no Équipe or Archivage; an archived cohort is read-only
 * and offers « Restaurer » to the President. Hiding is a convenience, the API refuses the rest.
 *
 * Saving sends only what changed with the cohort's `revision`; if someone else changed it first the
 * server refuses (`409`) and the page reloads the cohort instead of overwriting their work.
 */
export function CohortSettingsScreen({ cohortId }: { cohortId: string }) {
  const cohort = useCohort(cohortId);

  if (cohort.isPending) return <SkeletonRows rows={4} />;
  if (cohort.isError) {
    return (
      <ErrorPanel
        isRetrying={cohort.isFetching}
        onRetry={() => {
          void cohort.refetch();
        }}
      />
    );
  }

  // Keyed on the revision: after a save (or a reload that found a newer version) the form starts
  // again from what the server holds.
  return <SettingsForm key={cohort.data.revision} cohort={cohort.data} />;
}
